import { FormControlLabel, Stack, Switch } from "@mui/material";
import { useId } from "react";
import { Control, Controller, FieldValues } from "react-hook-form";
import {
  CreateCardRequest,
  EditCardRequest,
} from "tng-address-collection-shared/types/apiTypes";
import { unknownEmployee } from "tng-address-collection-shared/unknownEmployee";
import { useEligibleSenderIds } from "../hooks/useEligibleSenderIds";
import { useGetEmployees } from "../hooks/useGetEmployees";
import { useTranslator } from "../i18n/translator";
import { AutocompleteFormField } from "./AutocompleteFormField";
import { InfoToolTip } from "./InfoTooltip";

interface CardFieldsProps<T extends FieldValues> {
  control: Control<T>;
  isFieldsDisabled: boolean;
}

export const CardFields = ({
  control,
  isFieldsDisabled,
}: CardFieldsProps<CreateCardRequest | EditCardRequest>) => {
  const translator = useTranslator();
  const senderHelpId = useId();
  const receivesGiftHelpId = useId();
  const automaticDispatchHelpId = useId();
  const { data: employees } = useGetEmployees();
  const { data: eligibleSenderIds, isFetching: isFetchingEligibleSenderIds } =
    useEligibleSenderIds();

  return (
    <Stack spacing={2}>
      <AutocompleteFormField
        name="senderId"
        control={control}
        disabled={isFieldsDisabled}
        label={
          <span>
            {translator("sender")}
            <InfoToolTip
              helpertext={translator("senderHelperText")}
              ariaDescribedById={senderHelpId}
            />
          </span>
        }
        ariaDescribedBy={senderHelpId}
        options={eligibleSenderIds ?? []}
        noOptionsText={translator(
          isFetchingEligibleSenderIds ? "loading" : "noOptions",
        )}
        getOptionKey={(option) =>
          option.type === "email"
            ? `DIST${option.email}`
            : `UID${option.uidNumber}`
        }
        getOptionLabel={(option) =>
          option.type === "email"
            ? option.email
            : (
                employees?.find((x) => x.uidNumber === option.uidNumber) ??
                unknownEmployee(option.uidNumber)
              ).name
        }
        isOptionEqualToValue={(option, value) =>
          (option.type === "employee" &&
            value.type === "employee" &&
            option.uidNumber === value.uidNumber) ||
          (option.type === "email" &&
            value.type === "email" &&
            option.email === value.email)
        }
      />

      <Stack spacing={1}>
        <FormControlLabel
          control={
            <Controller
              defaultValue={false}
              name="receivesGift"
              disabled={isFieldsDisabled}
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  checked={field.value}
                  slotProps={{
                    input: { "aria-describedby": receivesGiftHelpId },
                  }}
                />
              )}
            />
          }
          label={
            <span>
              {translator("receivesGift")}
              <InfoToolTip
                helpertext={translator("receivesGiftHelperText")}
                ariaDescribedById={receivesGiftHelpId}
              />
            </span>
          }
        />
        <FormControlLabel
          control={
            <Controller
              defaultValue={false}
              disabled={isFieldsDisabled}
              name="manualDispatch"
              control={control}
              render={({ field }) => (
                // Shows "Automatic dispatch" but stores manualDispatch value as the inverse of the visible switch state
                <Switch
                  {...field}
                  checked={!field.value}
                  onChange={(_, checked) => field.onChange(!checked)}
                  slotProps={{
                    input: { "aria-describedby": automaticDispatchHelpId },
                  }}
                />
              )}
            />
          }
          label={
            <span>
              {translator("automaticDispatch")}
              <InfoToolTip
                helpertext={translator("automaticDispatchHelperText")}
                ariaDescribedById={automaticDispatchHelpId}
              />
            </span>
          }
        />
      </Stack>
    </Stack>
  );
};
